import {
  AI_BRAND,
  AI_FAQ_ENTRIES,
  AI_SAMPLE_QUERIES,
  AI_POPULAR_DREAMS,
  buildDreamFaqEntry,
  buildNumberFaqEntry,
  buildHowToSteps,
} from './aiKnowledge';

const SITE_URL = 'https://hotto.app';
const SITE_NAME = 'Hotto';
const DEFAULT_OG_IMAGE =
  'https://hotto.app/api/topten/lotto/image/hint?type=CALENDAR_KUMCHANOD&id=CALENDAR_KUMCHANOD';

export function getSearchMode(keyword) {
  if (!keyword) return 'default';
  if (!isNaN(+keyword.trim())) return 'number';
  return 'dream';
}

export function buildCanonicalUrl(mode, keyword) {
  if (mode === 'number' || mode === 'dream') {
    return `${SITE_URL}/search/${encodeURIComponent(keyword.trim())}`;
  }
  if (mode === 'article' && keyword) {
    return `${SITE_URL}/article?slug=${encodeURIComponent(keyword)}`;
  }
  if (mode === 'tarot') {
    return `${SITE_URL}/tarot`;
  }
  return SITE_URL;
}

export function buildTarotCanonicalUrl() {
  return `${SITE_URL}/tarot`;
}

export function buildTarotPageTitle() {
  return `ไพ่ยิปซีออนไลน์ฟรี เลขนำโชคจากไพ่ | คำทำนายละเอียด | ${SITE_NAME}`;
}

export function buildTarotPageDescription() {
  return 'เปิดไพ่ยิปซี Major Arcana 22 ใบ ฟรี รับคำทำนายละเอียดด้านความรัก การงาน การเงิน เชื่อมเลขนำโชคกับสถิติหวย 32 ปี แชร์ผลให้เพื่อนบน Hotto.app';
}

export function buildTarotKeywords() {
  return [
    'ไพ่ยิปซี',
    'ไพ่ทาโร่',
    'ทำนายไพ่',
    'คำทำนาย',
    'เลขนำโชค',
    'เลขเด็ด',
    'ทำนายฝัน',
    'hotto',
  ].join(', ');
}

export function buildPageTitle(mode, keyword, articleTitle) {
  if (mode === 'article' && articleTitle) return `${articleTitle} | ${SITE_NAME}`;
  if (mode === 'article' && keyword) return `${SITE_NAME} | บทความเลขเด็ด ${keyword}`;
  if (mode === 'number') {
    return `เลข ${keyword} ถูกบ่อยไหม? สถิติหวย ${keyword} 32 ปี | ${SITE_NAME}`;
  }
  if (mode === 'dream') {
    return `ฝันเห็น${keyword} เลขเด็ด ${keyword} | ทำนายฝัน เลขนำโชค | ${SITE_NAME}`;
  }
  return `${SITE_NAME} | ทำนายฝัน หาเลขเด็ดงวดนี้ เช็คสถิติหวย 32 ปี ฟรี`;
}

export function buildPageDescription(mode, keyword, articleTeaser) {
  if (mode === 'article' && articleTeaser) return articleTeaser;
  if (mode === 'article' && keyword) {
    return `อ่านบทความเลขเด็ด ${keyword} สถิติหวย เคล็ดลับเพิ่มโอกาสถูกรางวัล จาก Hotto.app`;
  }
  if (mode === 'number') {
    return `เช็คสถิติเลข ${keyword} ถูกรางวัลบ่อยแค่ไหน ดูประวัติย้อนหลัง 32 ปี ความฝันที่เกี่ยวข้อง และเลขเด็ดคำชะโนด ปฏิทินจีน ฟรีที่ Hotto`;
  }
  if (mode === 'dream') {
    return `ฝันเห็น${keyword} ฝันถึง${keyword} เลขเด็ดอะไร? ดูเลขนำโชค เลขหลัก เลขรอง คำทำนายฝัน และสถิติหวยย้อนหลัง 32 ปี ฟรีที่ Hotto.app`;
  }
  return 'ทำนายฝัน หาเลขเด็ดงวดนี้ เลขคำชะโนด ปฏิทินจีน เช็คสถิติหวยย้อนหลัง 32 ปี ตรวจผลรางวัลที่หนึ่งและสองตัวล่าง ฟรีที่ Hotto.app';
}

export function buildKeywords(mode, keyword) {
  const base = [
    'เลขเด็ด',
    'ทำนายฝัน',
    'ฝันว่า',
    'เช็คหวย',
    'ตรวจหวย',
    'เลขเด็ดงวดนี้',
    'เลขคำชะโนด',
    'ปฏิทินจีน',
    'สถิติหวย',
    'hotto',
  ];
  if (mode === 'number') return [...base, `เลข ${keyword}`, `สถิติเลข ${keyword}`].join(', ');
  if (mode === 'dream') {
    return [...base, `ฝันเห็น${keyword}`, `ฝันถึง${keyword}`, `ฝันว่า${keyword}`].join(', ');
  }
  if (mode === 'article' && keyword) {
    return [...base, `บทความ ${keyword}`, 'บทความเลขเด็ด', 'สถิติหวย'].join(', ');
  }
  return base.join(', ');
}

export function buildArticleStructuredData({ slug, title, description, image, publishedAt, authorName }) {
  const url = buildCanonicalUrl('article', slug);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url,
    image: image || DEFAULT_OG_IMAGE,
    datePublished: publishedAt,
    author: { '@type': 'Person', name: authorName || SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` },
    },
    inLanguage: 'th-TH',
    mainEntityOfPage: url,
  };
}

function buildFaqSchema(mode, keyword, description) {
  const entries = [...AI_FAQ_ENTRIES];
  if (mode === 'dream' && keyword) {
    const dreamEntry = buildDreamFaqEntry(keyword);
    entries.push({ question: dreamEntry.question, answer: dreamEntry.answer, url: dreamEntry.url });
  }
  if (mode === 'number' && keyword) {
    const numberEntry = buildNumberFaqEntry(keyword);
    entries.push({ question: numberEntry.question, answer: numberEntry.answer, url: numberEntry.url });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: entry.answer,
        ...(entry.url ? { url: entry.url } : {}),
      },
    })),
  };
}

export function buildStructuredData(mode, keyword, extras = {}) {
  const url = mode === 'tarot' ? buildTarotCanonicalUrl() : buildCanonicalUrl(mode, keyword);
  const description = mode === 'tarot'
    ? buildTarotPageDescription()
    : buildPageDescription(mode, keyword, extras.articleTeaser);
  const pageTitle = mode === 'tarot'
    ? buildTarotPageTitle()
    : buildPageTitle(mode, keyword, extras.articleTitle);

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: ['hotto.app', 'Hotto.app'],
    url: SITE_URL,
    description: AI_BRAND.tagline,
    inLanguage: 'th-TH',
    about: {
      '@type': 'Thing',
      name: 'ทำนายเลขเด็ดจากฝันและสถิติหวยไทย',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search/{search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    description: AI_BRAND.tagline,
    sameAs: ['https://line.me/R/ti/p/%40140ffoit'],
    contactPoint: {
      '@type': 'ContactPoint',
      email: AI_BRAND.contactEmail,
      contactType: 'customer support',
      availableLanguage: 'Thai',
    },
  };

  const webApplication = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: SITE_NAME,
    url: SITE_URL,
    applicationCategory: 'ReferenceApplication',
    operatingSystem: 'Web',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'THB' },
    description: AI_BRAND.tagline,
    inLanguage: 'th-TH',
    featureList: [
      'ทำนายเลขเด็ดจากความฝัน',
      'ไพ่ยิปซีเลขนำโชคออนไลน์ฟรี',
      'เลขคำชะโนดและปฏิทินจีน',
      'สถิติหวยย้อนหลัง 32 ปี',
      'ผลรางวัลที่หนึ่งและสองตัวล่าง',
    ],
  };

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    description,
    url,
    inLanguage: 'th-TH',
    isPartOf: { '@type': 'WebSite', url: SITE_URL, name: SITE_NAME },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.page-heading', '.ai-reference__question', '.result-page-heading'],
    },
    ...(mode === 'default'
      ? { primaryImageOfPage: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE } }
      : {}),
  };

  const howTo = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'วิธีหาเลขเด็ดจากฝันด้วย Hotto',
    description: 'ขั้นตอนการค้นหาเลขนำโชคจากความฝันหรือเช็คสถิติเลขบน hotto.app',
    inLanguage: 'th-TH',
    totalTime: 'PT1M',
    step: buildHowToSteps().map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      ...(step.url ? { url: step.url } : {}),
    })),
  };

  const faq = buildFaqSchema(mode, keyword, description);

  const schemas = [website, organization, webApplication, webPage, howTo, faq];

  if (mode === 'default') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'ตัวอย่างคำถามเลขเด็ดที่ Hotto ตอบได้',
      itemListElement: AI_SAMPLE_QUERIES.map((sample, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: sample.query,
        url: sample.url,
      })),
    });
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'ความฝันยอดนิยมบน Hotto',
      itemListElement: AI_POPULAR_DREAMS.map((dream, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: `ฝันเห็น${dream.keyword}`,
        url: dream.url,
      })),
    });
  }

  if (mode === 'dream' && keyword) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      mainEntity: {
        '@type': 'Question',
        name: `ฝันเห็น${keyword} เลขเด็ดคืออะไร?`,
        text: `ผู้ใช้ถามเลขเด็ดจากฝันเกี่ยวกับ ${keyword}`,
        answerCount: 1,
        acceptedAnswer: {
          '@type': 'Answer',
          text: description,
          url,
        },
      },
    });
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Hotto', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'ทำนายฝัน', item: `${SITE_URL}/search/${encodeURIComponent(keyword)}` },
      ],
    });
  }

  if (mode === 'number' && keyword) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      mainEntity: {
        '@type': 'Question',
        name: `เลข ${keyword} ถูกรางวัลบ่อยแค่ไหน?`,
        answerCount: 1,
        acceptedAnswer: {
          '@type': 'Answer',
          text: description,
          url,
        },
      },
    });
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Hotto', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: `สถิติเลข ${keyword}`, item: url },
      ],
    });
  }

  if (mode === 'article' && extras.articleTitle) {
    schemas.push(
      buildArticleStructuredData({
        slug: keyword,
        title: extras.articleTitle,
        description,
        image: extras.articleImage,
        publishedAt: extras.publishedAt,
        authorName: extras.authorName,
      })
    );
  }

  if (mode === 'tarot') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'วิธีเปิดไพ่ยิปซีเลขนำโชคบน Hotto',
      description: 'เปิดไพ่ 3 ใบ รับคำทำนายและเลขนำโชคเชื่อมสถิติหวย',
      inLanguage: 'th-TH',
      totalTime: 'PT3M',
      step: [
        { '@type': 'HowToStep', position: 1, name: 'เลือกรูปแบบไพ่', text: 'เลือกเลขนำโชคจากไพ่ หรือไพ่ประจำวัน' },
        { '@type': 'HowToStep', position: 2, name: 'สับและเลือกไพ่', text: 'ตั้งจิตแล้วแตะไพ่ตามสัญชาตญาณ' },
        { '@type': 'HowToStep', position: 3, name: 'อ่านคำทำนาย', text: 'ดูคำทำนายละเอียดและเลขนำโชคจากไพ่' },
        { '@type': 'HowToStep', position: 4, name: 'เช็คเลขบน Hotto', text: 'คลิกเลขเพื่อเช็คสถิติหวย 32 ปี', url: `${SITE_URL}/tarot` },
      ],
    });
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Hotto', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'ไพ่ยิปซี', item: `${SITE_URL}/tarot` },
      ],
    });
  }

  return schemas;
}

export { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE };
